import { ChangeDetectorRef, Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, takeUntil } from 'rxjs/operators';

import { IOrderUser } from 'src/app/features/orders/models/order.model';
import { OrdersService } from 'src/app/features/orders/services/orders.services';
import { getUserColor, getUserInitials } from 'src/app/features/smart-inbox/utils/user.util';
import { requiredTrimValidator } from 'src/app/shared/validators/fields-validators';
import { SharedDialogOptions } from 'src/app/ui/shared-dialog/shared-dialog.component';
import { ITeam } from '../../../../models/settings.models';

export interface TeamFormDialogData {
  team: ITeam | null;
}

export interface TeamFormPayload {
  name: string;
  description: string;
  member_ids: number[];
}

/**
 * The only user fields this dialog needs. `ITeam.members` carries `{ id, name, image }`
 * and nothing else, so the selection is typed down to that intersection rather than to
 * `IUser`/`IOrderUser` — reading `email` off a seeded member would be `undefined`.
 */
export type TeamMemberOption = Pick<IOrderUser, 'id' | 'name' | 'image'>;

@Component({
  selector: 'app-team-form-dialog',
  templateUrl: './team-form-dialog.component.html',
  styleUrls: ['./team-form-dialog.component.scss'],
})
export class TeamFormDialogComponent implements OnInit, OnDestroy {
  /** The parent owns the store; this dialog only reports what the user filled in. */
  @Output() save = new EventEmitter<TeamFormPayload>();

  team: ITeam | null = null;

  readonly teamNameControl = new FormControl('', requiredTrimValidator());
  readonly teamDescriptionControl = new FormControl('');

  selectedMembers: TeamMemberOption[] = [];
  searchResults: IOrderUser[] = [];
  searchTerm = '';
  loadingOptions = false;
  isSubmitting = false;

  readonly getUserInitials = getUserInitials;
  readonly getUserColor = getUserColor;

  private readonly search$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) data: TeamFormDialogData,
    private readonly dialogRef: MatDialogRef<TeamFormDialogComponent>,
    private readonly translateService: TranslateService,
    private readonly ordersService: OrdersService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.team = data?.team ?? null;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.teamNameControl.setValue(this.team?.name ?? '');
    this.teamDescriptionControl.setValue(this.team?.description ?? '');
    this.selectedMembers = (this.team?.members ?? []).map((member) => ({
      id: member.id,
      name: member.name,
      image: member.image,
    }));

    this.search$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((query) => {
          this.loadingOptions = true;
          this.cdr.markForCheck();
          return this.ordersService.searchBackofficeUsers(query, 20).pipe(
            // Caught on the INNER call, so the failure ends this search and not the
            // outer subscription. `takeUntil` does not stop an error, and the service
            // only maps: without this one failed request unsubscribes the picker for
            // the life of the dialog, and every later keystroke does nothing. The
            // interceptor already toasts the error, so the empty list is enough here.
            catchError(() => of([] as IOrderUser[])),
            finalize(() => {
              this.loadingOptions = false;
              this.cdr.markForCheck();
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((users) => {
        this.searchResults = users;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Dialog chrome ─────────────────────────────────────────────────────────
  get isEditMode(): boolean {
    return !!this.team;
  }

  get dialogTitle(): string {
    const action = this.isEditMode ? 'common.edit' : 'common.add';
    return `${this.translateService.instant(action)} ${this.translateService.instant('settings.team')}`;
  }

  get saveLabel(): string {
    return this.translateService.instant(this.isEditMode ? 'common.save' : 'common.add');
  }

  get isFormInvalid(): boolean {
    return this.teamNameControl.invalid;
  }

  get dialogOptions(): SharedDialogOptions {
    return {
      size: 'medium',
      cancelText: this.translateService.instant('common.cancel'),
      customConfirmButton: true,
      hideConfirmButton: true,
    };
  }

  // ── Member picker ─────────────────────────────────────────────────────────
  onSearch(term: string): void {
    this.searchTerm = term ?? '';
    this.search$.next(this.searchTerm.trim());
  }

  isSelected(user: TeamMemberOption): boolean {
    return this.selectedMembers.some((member) => member.id === user.id);
  }

  toggleMember(user: TeamMemberOption): void {
    if (this.isSelected(user)) {
      this.removeMember(user);
      return;
    }
    this.selectedMembers = [
      ...this.selectedMembers,
      { id: user.id, name: user.name, image: user.image },
    ];
  }

  removeMember(user: TeamMemberOption): void {
    this.selectedMembers = this.selectedMembers.filter((member) => member.id !== user.id);
  }

  trackByUserId(_: number, user: TeamMemberOption): number {
    return user.id;
  }

  // ── Submission ────────────────────────────────────────────────────────────
  submitForm(): void {
    this.teamNameControl.markAsTouched();
    if (this.isFormInvalid || this.isSubmitting) return;

    const name = (this.teamNameControl.value ?? '').trim();
    if (!name) return;

    this.isSubmitting = true;
    this.save.emit({
      name,
      description: (this.teamDescriptionControl.value ?? '').trim(),
      // Always emitted, empty included. The PATCH keys off presence, so `[]` is the
      // only way to clear a team's membership; omitting the key would keep it.
      member_ids: this.selectedMembers.map((member) => member.id),
    });
  }

  close(): void {
    if (this.isSubmitting) return;
    this.dialogRef.close();
  }

  setSubmitting(isSubmitting: boolean): void {
    this.isSubmitting = isSubmitting;
    this.cdr.markForCheck();
  }
}
